import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { describeGenericApiError, extractApiErrorMessage } from '../../core/http/api-error.util';


/**
 * Component responsible for handling user login. It provides a form for users to enter their email
 * address and submit it to the authentication service. If the user is already authenticated, it
 * redirects them to the return URL or the default '/today' page. It also handles displaying loading
 * state and error messages during the login process.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  email = 'test@lumen.local';
  password = '';
  loading = signal(false);
  error = signal<string | null>(null);

  ngOnInit(): void {
    if (this.auth.isAuthenticated()) {
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/today';
      void this.router.navigateByUrl(returnUrl);
    }
  }

  submit(): void {
    const trimmed = this.email?.trim();
    if (!trimmed) {
      this.error.set('Email is required');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    const trimmedPassword = this.password?.trim();
    this.auth.login(trimmed, trimmedPassword).subscribe({
      next: () => {
        this.loading.set(false);
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/today';
        this.router.navigateByUrl(returnUrl);
      },
      error: (err) => {
        this.loading.set(false);
        const msg = extractApiErrorMessage(err) ?? describeGenericApiError(err, 'Login failed');
        this.error.set(msg);
      },
    });
  }
}
